import React, { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { BellRing, FileSignature, LayoutDashboard, Smartphone, Video } from "lucide-react";
import { DoctorPortrait, PrescriptionPad, VideoCallScene } from "./Telehealth";

/** Patient's phone: the "doctor is waiting" alert and one-tap join. */
const PhoneSlide = () => (
  <div className="flex items-center justify-center gap-6 py-2 sm:gap-10">
    <div className="relative w-[15.5rem] shrink-0 rounded-[2.6rem] bg-ink-900 p-2.5 shadow-[0_40px_90px_-30px_rgba(11,35,73,.6)] sm:w-[17rem]">
      <div className="relative overflow-hidden rounded-[2.1rem] bg-slate-950">
        <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
        <div className="aspect-[9/17]">
          <DoctorPortrait />
        </div>
        <div className="absolute inset-x-3 top-10 flex items-center gap-2.5 rounded-2xl bg-emerald-600/95 p-2.5 text-white shadow-xl backdrop-blur">
          <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20">
            <Video className="h-4 w-4" />
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-ping rounded-full bg-white" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold leading-tight">Dr. Meera Iyer is waiting</p>
            <p className="text-[10px] text-white/80">Video consultation</p>
          </div>
          <span className="rounded-lg bg-white px-2 py-1 text-[10px] font-bold text-emerald-700">Join</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-5 pt-14 text-white">
          <p className="text-sm font-semibold">Dr. Meera Iyer</p>
          <p className="text-[11px] text-white/70">General Medicine · 04:12</p>
          <div className="mt-3 flex justify-center gap-3">
            {["bg-white/20", "bg-white/20", "bg-rose-600"].map((c, i) => <span key={i} className={`h-9 w-9 rounded-full ${c}`} />)}
          </div>
        </div>
      </div>
    </div>
    <ul className="hidden max-w-[14rem] space-y-3 sm:block">
      {[
        [BellRing, "Alert when the doctor is ready", "On any page of the patient app"],
        [Smartphone, "Join in one tap", "No downloads, works on any phone"],
        [FileSignature, "Prescription in the app", "Before the call even ends"],
      ].map(([Icon, t, s]) => (
        <li key={t} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Icon className="h-4 w-4" /></span>
          <span>
            <span className="block text-sm font-semibold text-ink-900">{t}</span>
            <span className="block text-xs text-slate-500">{s}</span>
          </span>
        </li>
      ))}
    </ul>
  </div>
);

/** Rotating hero visual: video consultation first, then the rest of the platform. */
const HeroSlider = ({ dashboard }) => {
  const reduce = useReducedMotion();
  const slides = [
    { key: "call", icon: Video, label: "HD video consultations", body: <VideoCallScene bare /> },
    { key: "phone", icon: Smartphone, label: "Patients join from their phone", body: <PhoneSlide /> },
    { key: "rx", icon: FileSignature, label: "Digital prescriptions", body: <div className="mx-auto max-w-md"><PrescriptionPad bare /></div> },
    { key: "ops", icon: LayoutDashboard, label: "Run the whole hospital", body: dashboard },
  ];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (reduce || paused) return undefined;
    const t = setTimeout(() => setActive((a) => (a + 1) % slides.length), 5500);
    return () => clearTimeout(t);
  }, [active, paused, reduce, slides.length]);

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      {/* all slides share one grid cell, so the height never jumps */}
      <div className="grid items-start lg:items-center" aria-roledescription="carousel" aria-label="Product highlights">
        {slides.map((s, i) => (
          <div
            key={s.key}
            aria-hidden={i !== active}
            className={`col-start-1 row-start-1 transition-all duration-700 ease-out ${i === active ? "z-10 translate-y-0 scale-100 opacity-100" : "pointer-events-none z-0 translate-y-3 scale-[0.97] opacity-0"}`}
          >
            {s.body}
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap justify-center gap-2" role="tablist">
        {slides.map((s, i) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={i === active}
            onClick={() => setActive(i)}
            className={`relative inline-flex items-center gap-1.5 overflow-hidden rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
              i === active ? "border-brand-200 bg-white text-brand-700 shadow-sm" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <s.icon className="h-3.5 w-3.5" />
            <span className={i === active ? "" : "hidden sm:inline"}>{s.label}</span>
            {i === active && !reduce && (
              <span key={`${active}-${paused}`} className={`absolute inset-x-0 bottom-0 h-0.5 origin-left bg-brand-500 ${paused ? "" : "animate-[slide-progress_5.5s_linear_forwards]"}`} />
            )}
          </button>
        ))}
      </div>
      <style>{"@keyframes slide-progress{from{transform:scaleX(0)}to{transform:scaleX(1)}}"}</style>
    </div>
  );
};

export default HeroSlider;
